import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Trophy, Gift, Crown, Sparkles, Lock, TrendingUp } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function periodLabel(p) {
  if (!p) return '';
  const [y, m] = p.split('-').map(Number);
  return `${MONTH_NAMES[m - 1] || m} ${y}`;
}

function daysLeftInMonth() {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
}

export default function PrizesPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get('/prizes/current');
        setData(d);
      } catch (e) {
        if (e?.response?.status === 403) setForbidden(true);
      } finally { setLoading(false); }
    })();
  }, []);

  const daysLeft = useMemo(() => daysLeftInMonth(), []);

  if (loading) return <div className="min-h-screen bg-aura-bg grid place-items-center text-4xl text-aura-textDim animate-aura-pulse">⚡</div>;

  if (forbidden) {
    return (
      <div className="min-h-screen bg-aura-bg pb-28 relative">
        <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
        <header className="relative flex items-center justify-between px-5 pt-6">
          <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
            <ChevronLeft size={20} />
          </button>
          <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">PREMIOS</div>
          <span className="w-8" />
        </header>
        <main className="relative mx-auto max-w-md px-5 pt-8">
          <div className="aura-card p-6 text-center" data-testid="elite-only">
            <Lock size={32} className="text-aura-gold mx-auto mb-3" />
            <h1 className="font-display font-black text-white text-2xl">Módulo exclusivo ELITE</h1>
            <p className="text-sm text-aura-textDim mt-2">
              Este módulo solo está disponible para usuarios con plan ELITE activo.
              Activa tu ELITE por un pago único de <b className="text-aura-gold">$9 USD</b> y compite cada mes por 5 gadgets.
            </p>
            <Link to="/plans" data-testid="btn-goto-plans" className="aura-btn-primary mt-5 inline-flex">
              <Crown size={16} /> Activar ELITE
            </Link>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (!data) return null;

  const { period, started, notice, prizes, my_score, my_rank, top5 } = data;

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative overflow-x-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-aura-gold/25 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-aura-violet/20 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6 md:px-10">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">PREMIOS ELITE</div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-3xl px-5 md:px-10 pt-4 space-y-4">
        {/* Hero / status card */}
        <section className="aura-card p-5 relative overflow-hidden" data-testid="prizes-hero">
          <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-aura-gold/25 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-gold">
                Competencia mensual · {periodLabel(period)}
              </div>
              <h1 className="mt-2 font-display font-black text-white text-3xl leading-[0.95]">
                Tus puntos de <span className="aura-text-gradient">batalla</span> del mes
              </h1>
              <div className="mt-4 font-mono font-black text-white text-5xl" data-testid="my-score">
                {(my_score || 0).toLocaleString()}
              </div>
              <div className="text-[11px] text-aura-textDim">
                {started ? `AURA ganados en batallas este mes` : 'Arranca en Octubre 2026'}
              </div>
              {started && my_rank && (
                <div className="mt-2 inline-flex items-center gap-2 text-xs bg-aura-cyan/10 border border-aura-cyan/30 rounded-full px-3 py-1" data-testid="my-rank">
                  <TrendingUp size={12} className="text-aura-cyan" />
                  <span className="text-white font-display font-bold">Estás en el puesto #{my_rank}</span>
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="grid place-items-center h-16 w-16 rounded-2xl bg-aura-gradient text-black shadow-neon-cyan">
                <Crown size={28} strokeWidth={2.4} />
              </div>
              {started && (
                <div className="mt-3 text-center">
                  <div className="font-mono font-black text-white text-2xl">{daysLeft}</div>
                  <div className="text-[9px] uppercase tracking-widest text-aura-textDim">días</div>
                </div>
              )}
            </div>
          </div>

          {notice && (
            <div className="relative mt-4 rounded-xl bg-aura-violet/10 border border-aura-violet/30 p-3 text-xs text-white flex items-start gap-2" data-testid="program-notice">
              <Sparkles size={14} className="text-aura-violet mt-0.5" /> {notice}
            </div>
          )}
        </section>

        {/* Prizes for this month */}
        <section className="aura-card p-5" data-testid="prizes-slots">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-gold">Premios · {periodLabel(period)}</div>
              <div className="font-display font-black text-white text-lg">TOP 5 se los llevan</div>
            </div>
            <Gift size={22} className="text-aura-gold" />
          </div>

          {(!prizes || prizes.length === 0) ? (
            <div className="text-center text-sm text-aura-textDim py-6" data-testid="prizes-empty">
              El equipo AURA aún no publicó los premios de este mes. Vuelve pronto.
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 gap-3" data-testid="prizes-list">
              {prizes.map((p) => (
                <li key={p.rank} className="rounded-xl border border-aura-gold/25 bg-aura-gold/5 p-3 flex items-center gap-3" data-testid={`prize-${p.rank}`}>
                  <div className="h-10 w-10 rounded-xl bg-aura-gold text-black font-display font-black text-lg grid place-items-center shrink-0">
                    #{p.rank}
                  </div>
                  {p.image_url && (
                    <img src={p.image_url} alt={p.title} className="h-14 w-14 rounded-lg object-cover" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-black text-white truncate">{p.title}</div>
                    {p.description && <div className="text-[11px] text-aura-textDim truncate">{p.description}</div>}
                    {p.approx_value_usd != null && (
                      <div className="text-[10px] font-mono text-aura-gold">≈ ${Number(p.approx_value_usd).toLocaleString()} USD</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Top 5 leaderboard */}
        <section className="aura-card p-5" data-testid="prizes-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-cyan">TOP 5 ELITE</div>
              <div className="font-display font-black text-white text-lg">Lideres del mes</div>
            </div>
            <Trophy size={22} className="text-aura-gold" />
          </div>
          {(!top5 || top5.length === 0) ? (
            <div className="text-center text-sm text-aura-textDim py-8" data-testid="leaderboard-empty">
              {started ? 'Aún nadie ha ganado batallas este mes. ¡Sé el primero!' : 'La competencia arranca en Octubre 2026.'}
            </div>
          ) : (
            <ol className="space-y-2" data-testid="leaderboard-list">
              {top5.map((u, i) => {
                const isMe = user && u.user_id === user.user_id;
                return (
                  <li key={u.user_id} className={`flex items-center gap-3 p-3 rounded-xl border ${isMe ? 'border-aura-cyan/60 bg-aura-cyan/10' : 'border-white/5 bg-white/[0.02]'}`}>
                    <div className={`h-9 w-9 rounded-xl grid place-items-center font-display font-black text-lg shrink-0 ${
                      i === 0 ? 'bg-aura-gold text-black' :
                      i === 1 ? 'bg-white/20 text-white' :
                      i === 2 ? 'bg-orange-600/40 text-white' :
                      'bg-white/5 text-white/70'
                    }`}>
                      {i + 1}
                    </div>
                    <UserAvatar user={u} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-black text-white truncate">{u.name || `@${u.username}`}</div>
                      <div className="text-[11px] text-aura-textDim truncate">@{u.username} {u.city ? `· ${u.city}` : ''}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-black text-aura-cyan">{Number(u.score || 0).toLocaleString()}</div>
                      <div className="text-[9px] uppercase tracking-widest text-aura-textDim">pts</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {/* Rules */}
        <section className="aura-card p-5" data-testid="prizes-rules">
          <div className="font-display font-black text-white text-sm tracking-widest mb-3">CÓMO GANAR</div>
          <ul className="space-y-2 text-sm text-white/85">
            <li className="flex items-start gap-2"><span className="text-aura-gold">1.</span> Solo cuentan los puntos que <b>ganes en batallas</b> durante este mes (pot de apuestas). Cero puntos por reconocimientos, publicaciones o cualquier otra vía.</li>
            <li className="flex items-start gap-2"><span className="text-aura-gold">2.</span> Los puntos del mes anterior <b>no se acumulan</b>. Cada mes arranca en 0.</li>
            <li className="flex items-start gap-2"><span className="text-aura-gold">3.</span> Al cerrar el mes, los <b>5 usuarios ELITE con más puntos</b> ganan los premios listados arriba.</li>
            <li className="flex items-start gap-2"><span className="text-aura-gold">4.</span> El programa arranca oficialmente en <b className="text-aura-gold">Octubre 2026</b>.</li>
          </ul>
        </section>
      </main>
      <BottomNav />
    </div>
  );
}
